import { useEffect, useRef, useState } from 'react';
import { Checkbox, Header } from 'semantic-ui-react';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { KEY_DETAILS_GRID } from './KeyDetails';
import { withForm } from '~/utils/withForm';
import { useForm, useProjectContext } from '~/hooks';
import { useUserContext } from '~/hooks/providers/UserContextProvider';
import { setCompletionState } from '~/api/projects';
import { DealStates, DevelopmentStages } from '~/constants';
import DealLifecycleStepper from '~/components/DealLifecycleStepper';

interface ProjectCompletionStatesFormState {
  completionState: 'NONE' | 'DOCUMENTS_SIGNED' | 'CONSTRUCTION_COMPLETE' | 'DEAL_COMPLETE' | null;
}

const ProjectCompletionStates = withForm({ defaults: {} }, () => {
  const { permissions } = usePermissions();
  const [paddingLeft, setPaddingLeft] = useState('0');
  const { project, updateProjectValue, projectState, projectDisplayValues } = useProjectContext();
  const { loggedInUser } = useUserContext();
  const viewerIsInvestor = !!loggedInUser?.isInvestor;
  const { formState, updateFormValue } = useForm<ProjectCompletionStatesFormState>();
  const completionStateRef = useRef<string | null>(project?.completionState?.code);

  const { mutateAsync: updateProjectCompletionState, status } = useMutation((code: 'NONE' | 'DOCUMENTS_SIGNED' | 'CONSTRUCTION_COMPLETE' | 'DEAL_COMPLETE') => {
    return setCompletionState(project.id, code)
      .then(newProject => {
        updateProjectValue('', newProject, { doSave: false });
        completionStateRef.current = newProject?.completionState?.code;
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to update deal status');
        updateFormValue('completionState', completionStateRef.current);
      });
  });

  useEffect(() => {
    if (project?.completionState?.code) {
      updateFormValue('completionState', project.completionState.code);
    }
    completionStateRef.current = project?.completionState?.code;
  }, [project?.completionState?.code]);

  useEffect(() => {
    // Get key details grid.
    // If we can find the first element in the grid, offset the padding left of the inner componentry
    // so that it aligns with the key detail's second column.
    // If not found, there simply won't be padding. This ensures a graceful fallback for this component.
    const keyDetailsDiv = document.querySelector(`#${KEY_DETAILS_GRID} > div:first-child`) as HTMLDivElement;
    if (keyDetailsDiv?.offsetWidth && !isNaN(keyDetailsDiv.offsetWidth)) {
      setPaddingLeft(`${keyDetailsDiv.offsetWidth / 16}rem`);
    }
  }, []);

  if (project?.portfolioId) return null;

  // Keep the lifecycle graphic in sync with the status badge: a project that's
  // been released to investors reads "Bidding" on the badge even before any bids
  // land, but the server dealState only leaves MATCHING once there's an
  // investorId/bid. Bump it to AWAITING_BID so the stepper lands on "Bidding".
  const stepperDealState = projectDisplayValues?.status === 'Bidding'
    ? Math.max(projectState?.dealState ?? 0, DealStates.AWAITING_BID)
    : projectState?.dealState;

  return (
    <div className="card">
      <Header as='h2'>Deal status</Header>
      <DealLifecycleStepper
        dealState={stepperDealState}
        isDeclined={viewerIsInvestor && projectState?.isDeclined}
        isInvestor={viewerIsInvestor}
      />
      {permissions.canSetProjectCompletionState && (
      <div style={{ paddingLeft, display: 'flex', flexDirection: 'column' }}>
        <Header as='h3' style={{ marginTop: 'var(--medium)', marginBottom: 'var(--small)' }}>Actions</Header>
        {project.developmentStage === DevelopmentStages.OPERATING ? (
          <Checkbox
            label="Deal complete"
            style={{ margin: 'var(--small) 0' }}
            checked={formState.completionState === 'DEAL_COMPLETE'}
            onChange={() => {
              if (formState.completionState === 'DEAL_COMPLETE') {
                updateFormValue('completionState', null);
                updateProjectCompletionState('NONE');
              } else {
                updateFormValue('completionState', 'DEAL_COMPLETE');
                updateProjectCompletionState('DEAL_COMPLETE');
              }
            }}
            disabled={status === 'loading'}
          />
        ) : (
          <>
            <Checkbox
              label="Definitive documents signed"
              checked={!!formState.completionState}
              onChange={() => {
                if (formState.completionState === 'DOCUMENTS_SIGNED') {
                  updateFormValue('completionState', null);
                  updateProjectCompletionState('NONE');
                } else {
                  updateFormValue('completionState', 'DOCUMENTS_SIGNED');
                  updateProjectCompletionState('DOCUMENTS_SIGNED');
                }
              }}
              disabled={status === 'loading'}
            />
            <Checkbox
              label="Construction complete"
              style={{ margin: 'var(--small) 0' }}
              checked={formState.completionState === 'CONSTRUCTION_COMPLETE'}
              onChange={() => {
                if (formState.completionState === 'CONSTRUCTION_COMPLETE') {
                  updateFormValue('completionState', 'DOCUMENTS_SIGNED');
                  updateProjectCompletionState('DOCUMENTS_SIGNED');
                } else {
                  updateFormValue('completionState', 'CONSTRUCTION_COMPLETE');
                  updateProjectCompletionState('CONSTRUCTION_COMPLETE');
                }
              }}
              disabled={status === 'loading' || !formState.completionState}
            />
          </>
        )}


      </div>
      )}
    </div>
  );
});

export default ProjectCompletionStates;